import React, { useState, useEffect } from 'react';
import { BancaViewModel } from '../../../viewmodel/banca/BancaViewModel';
import { TipoBanca } from '../../../model/banca/Banca';
import { useProcessoContext } from '../../../Routes/context/ProcessoUsuarioContext';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../../components/BackendErrorMessage';
import AnimatedModal from './AnimatedModal';
import ModalHelp from '../../../components/ModalHelp';
import { Edital } from '../../../model/Edital';
import { EditalViewModel } from '../../../viewmodel/EditalViewModel';

interface CriarBancaModalProps {
  tiposBanca: TipoBanca[];
  onClose: () => void;
  onBancaCriada: () => void;
}

interface EditalChamada {
  editalId: string;
  numeroChamada?: number | null;
}

const CriarBancaModal: React.FC<CriarBancaModalProps> = ({
  tiposBanca,
  onClose,
  onBancaCriada
}) => {
  const { processo } = useProcessoContext();
  const [selectedTipoBanca, setSelectedTipoBanca] = useState<TipoBanca | null>(null);
  const [loading, setLoading] = useState(false);
  const [editais, setEditais] = useState<Edital[]>([]);
  const [selectedEditais, setSelectedEditais] = useState<EditalChamada[]>([]);
  const [etapa, setEtapa] = useState<1 | 2 | 3>(1);
  const [formaRealizacao, setFormaRealizacao] = useState<'PRESENCIAL' | 'REMOTO' | null>(null);
  const [hasIntegracao, setHasIntegracao] = useState<boolean | null>(null);
  const hasContasServico = processo?.contasServico && processo.contasServico.length > 0;

  useEffect(() => {
    if (selectedTipoBanca && processo) {
      loadEditais();
    }
  }, [selectedTipoBanca, processo]);

  const loadEditais = async () => {
    if (!selectedTipoBanca || !processo) return;

    try {
      const editaisData = await EditalViewModel.getEditaisWithChamadaAtual(processo.id, selectedTipoBanca.id!);
      setEditais(editaisData);
    } catch (error) {
      console.error('Erro ao carregar editais:', error);
      toast.error('Erro ao carregar editais');
    }
  };

  const handleProximaEtapa = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTipoBanca || !processo) {
      toast.error('Selecione um tipo de banca');
      return;
    }

    if (selectedEditais.length === 0) {
      toast.error('Selecione pelo menos um edital');
      return;
    }

    setEtapa(2);
  };

  const handleProximaEtapaFormaRealizacao = async () => {
    if (!formaRealizacao) {
      toast.error('Selecione a forma de realização');
      return;
    }

    if (hasContasServico) {
      setEtapa(3);
    } else {
      setHasIntegracao(false);
      await submitBanca();
    }
  };

  const submitBanca = async () => {
    if (!formaRealizacao || !selectedTipoBanca || !processo) return;

    setLoading(true);
    try {
      const editaisParaEnviar = selectedEditais.map(edital => ({
        editalId: edital.editalId,
        ...(edital.numeroChamada !== null && { numeroChamada: edital.numeroChamada })
      }));

      await BancaViewModel.createBancaWithEditais(processo.id, selectedTipoBanca, editaisParaEnviar, formaRealizacao, hasIntegracao ?? undefined);
      toast.success('Banca criada com sucesso!');
      onBancaCriada();
      onClose();
    } catch (error) {
      console.error('Erro ao criar banca:', error);
      toast.error(BackendErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitBanca();
  };

  const handleEditalToggle = (editalId: string, chamadaAtual?: number) => {
    setSelectedEditais(prev => {
      const exists = prev.find(e => e.editalId === editalId);
      if (exists) {
        return prev.filter(e => e.editalId !== editalId);
      } else {
        return [...prev, { editalId, numeroChamada: chamadaAtual || null }];
      }
    });
  };

  const handleChamadaChange = (editalId: string, numeroChamada: number | null) => {
    setSelectedEditais(prev =>
      prev.map(e =>
        e.editalId === editalId ? { ...e, numeroChamada } : e
      )
    );
  };

  const handleChamadaInputChange = (editalId: string, value: string) => {
    const raw = value.replace(/\D/g, ''); // remove não-dígitos
    const numeroChamada = parseInt(raw);
    if (!isNaN(numeroChamada) && numeroChamada >= 1) {
      handleChamadaChange(editalId, numeroChamada);
    } else if (raw === '') {
      // Se o usuário apagou tudo, definir como null
      handleChamadaChange(editalId, null);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatedModal isOpen={true} onClose={onClose}>
      <div style={{
        backgroundColor: 'white',
        padding: '30px',
        borderRadius: '8px',
        width: '400px',
        maxWidth: '90vw'
      }}>
        <h3 style={{ marginBottom: '20px' }}>Criar Nova Banca</h3>
        <ModalHelp text="Selecione o tipo de banca (Facial, Virtual ou Recursal), associe editais e configure a forma de realização (presencial ou remota)." />

        <form onSubmit={etapa === 1 ? handleProximaEtapa : handleSubmit}>
          {etapa === 1 && (
            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="tipoBanca" style={{ display: 'block', marginBottom: '8px' }}>
                Tipo de Banca:
              </label>
            <select
              id="tipoBanca"
              value={selectedTipoBanca?.nome || ''}
              onChange={(e) => {
                const tipo = tiposBanca.find(t => t.nome === e.target.value);
                setSelectedTipoBanca(tipo || null);
                setSelectedEditais([]);
                
                // Define forma de realização e integração baseada no tipo de banca
                if (tipo?.nome.includes('Facial')) {
                  setFormaRealizacao('PRESENCIAL');
                  setHasIntegracao(true);
                } else if (tipo?.nome.includes('Virtual') || tipo?.nome.includes('Recursal')) {
                  setFormaRealizacao('REMOTO');
                  setHasIntegracao(false);
                }
              }}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              required
            >
              <option value="">Selecione um tipo</option>
              {tiposBanca.map(tipo => (
                <option key={tipo.nome} value={tipo.nome}>
                  {tipo.nome.replace('Banca de Heteroidentificação ', '')}
                </option>
              ))}
            </select>
            </div>
          )}

          {etapa === 1 && editais.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px' }}>
                Editais e Chamadas:
              </label>
              <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '4px' }}>
              {editais.map(edital => {
                const isSelected = selectedEditais.some(e => e.editalId === edital.id);
                const selectedEdital = selectedEditais.find(e => e.editalId === edital.id);
                const numeroChamada = selectedEdital?.numeroChamada ?? 1;

                return (
                  <div key={edital.id} style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '10px',
                    marginBottom: '10px',
                    backgroundColor: isSelected ? '#f8f9fa' : 'white'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                      <input
                        type="checkbox"
                        id={`edital-${edital.id}`}
                        checked={isSelected}
                        onChange={() => handleEditalToggle(edital.id, edital.chamadaAtual)}
                        style={{ marginRight: '8px' }}
                      />
                      <label htmlFor={`edital-${edital.id}`} style={{ fontWeight: 'bold' }}>
                        {edital.nome}
                      </label>
                    </div>

                    {/* Exibir informação da chamada atual sempre */}
                    {edital.chamadaAtual && (
                      <div style={{ marginLeft: '24px', fontSize: '12px', color: '#28a745', fontWeight: 'bold' }}>
                        Última neste Edital: {edital.chamadaAtual}ª
                      </div>
                    )}

                    {/* Exibir seletor de chamada apenas quando selecionado */}
                    {edital.hasChamadas && isSelected && (
                      <div style={{ marginLeft: '24px', marginTop: '8px' }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '15px' }}>
                          Número da Chamada desta Banca:
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <button
                            type="button"
                            onClick={() => {
                              if (numeroChamada && numeroChamada > 1) {
                                handleChamadaChange(edital.id, numeroChamada - 1);
                              }
                            }}
                            style={{
                              width: '32px',
                              height: '32px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '16px',
                              fontWeight: 'bold'
                            }}
                            disabled={!numeroChamada || numeroChamada <= 1}
                          >
                            <i className="bi bi-chevron-left me-1"></i>
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={numeroChamada || ''}
                            onChange={(e) => handleChamadaInputChange(edital.id, e.target.value)}
                            style={{
                              width: '70px',
                              padding: '4px 8px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              textAlign: 'center',
                              MozAppearance: 'textfield',
                              WebkitAppearance: 'none',
                              appearance: 'none'
                            }}
                            onWheel={(e) => e.currentTarget.blur()}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => {
                              handleChamadaChange(edital.id, (numeroChamada || 0) + 1);
                            }}
                            style={{
                              width: '32px',
                              height: '32px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '16px',
                              fontWeight: 'bold'
                            }}
                          >
                            <i className="bi bi-chevron-right me-1"></i>
                          </button>
                        </div>
                      </div>
                    )}

                    {!edital.hasChamadas && (
                      <div style={{ marginLeft: '24px', fontSize: '14px', color: '#6c757d' }}>
                        Este edital não trabalha com chamadas
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            </div>
          )}

          {etapa === 1 && (
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #ddd',
                  background: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: '1px solid #28a745',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
                disabled={!selectedTipoBanca}
              >
                Próximo
              </button>
            </div>
          )}

          {etapa === 2 && (
            <>
              <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', fontSize: '14px' }}>
                <div><strong>Tipo de Banca:</strong> {selectedTipoBanca?.nome.replace('Banca de Heteroidentificação ', '')}</div>
                <div style={{ marginTop: '4px' }}><strong>Editais:</strong> {selectedEditais.length} selecionado(s)</div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontWeight: 'bold' }}>
                  Forma de Realização:
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    border: `2px solid ${formaRealizacao === 'REMOTO' ? '#28a745' : '#ddd'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: formaRealizacao === 'REMOTO' ? '#f0f9f4' : 'white'
                  }}>
                    <input
                      type="radio"
                      name="formaRealizacao"
                      value="REMOTO"
                      checked={formaRealizacao === 'REMOTO'}
                      onChange={() => setFormaRealizacao('REMOTO')}
                      style={{ marginRight: '10px' }}
                    />
                    <i className="bi bi-camera-video" style={{ fontSize: '20px', marginRight: '10px' }}></i>
                    <span style={{ fontWeight: formaRealizacao === 'REMOTO' ? 'bold' : 'normal' }}>
                      Remoto
                    </span>
                  </label>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    border: `2px solid ${formaRealizacao === 'PRESENCIAL' ? '#28a745' : '#ddd'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: formaRealizacao === 'PRESENCIAL' ? '#f0f9f4' : 'white'
                  }}>
                    <input
                      type="radio"
                      name="formaRealizacao"
                      value="PRESENCIAL"
                      checked={formaRealizacao === 'PRESENCIAL'}
                      onChange={() => setFormaRealizacao('PRESENCIAL')}
                      style={{ marginRight: '10px' }}
                    />
                    <i className="bi bi-people" style={{ fontSize: '20px', marginRight: '10px' }}></i>
                    <span style={{ fontWeight: formaRealizacao === 'PRESENCIAL' ? 'bold' : 'normal' }}>
                      Presencial
                    </span>
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setEtapa(1)}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    background: 'white',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  disabled={loading}
                >
                  Voltar
                </button>
                <button
                  type="button"
                  onClick={handleProximaEtapaFormaRealizacao}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: '1px solid #28a745',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  disabled={loading || !formaRealizacao}
                >
                  {hasContasServico ? 'Próximo' : 'Criar Banca'}
                </button>
              </div>
            </>
          )}

          {etapa === 3 && (
            <>
              <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', fontSize: '14px' }}>
                <div><strong>Tipo de Banca:</strong> {selectedTipoBanca?.nome.replace('Banca de Heteroidentificação ', '')}</div>
                <div style={{ marginTop: '4px' }}><strong>Editais:</strong> {selectedEditais.length} selecionado(s)</div>
                <div style={{ marginTop: '4px' }}><strong>Forma:</strong> {formaRealizacao === 'REMOTO' ? 'Remoto' : 'Presencial'}</div>
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '12px', fontWeight: 'bold' }}>
                  Esta banca terá integração com sistema externo? 
                </label>
                <label style={{ display: 'block', marginBottom: '12px', fontWeight: 'lighter' }}>(Envio de Candidatos, fotos ou outros dados)</label>
                {processo?.contasServico && processo.contasServico.length > 0 && (
                  <div style={{ marginBottom: '12px', fontSize: '14px', color: '#6c757d', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                    <strong>Contas disponíveis:</strong> {processo.contasServico.map(c => c.unidade.nome).join(', ')}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    border: `2px solid ${hasIntegracao === true ? '#28a745' : '#ddd'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: hasIntegracao === true ? '#f0f9f4' : 'white'
                  }}>
                    <input
                      type="radio"
                      name="hasIntegracao"
                      checked={hasIntegracao === true}
                      onChange={() => setHasIntegracao(true)}
                      style={{ marginRight: '10px' }}
                    />
                    <span style={{ fontWeight: hasIntegracao === true ? 'bold' : 'normal' }}>
                      Sim, esta banca terá integração
                    </span>
                  </label>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px',
                    border: `2px solid ${hasIntegracao === false ? '#28a745' : '#ddd'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundColor: hasIntegracao === false ? '#f0f9f4' : 'white'
                  }}>
                    <input
                      type="radio"
                      name="hasIntegracao"
                      checked={hasIntegracao === false}
                      onChange={() => setHasIntegracao(false)}
                      style={{ marginRight: '10px' }}
                    />
                    <span style={{ fontWeight: hasIntegracao === false ? 'bold' : 'normal' }}>
                      Não, esta banca não terá integração
                    </span>
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setEtapa(2)}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    background: 'white',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  disabled={loading}
                >
                  Voltar
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: '1px solid #28a745',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  disabled={loading || hasIntegracao === null}
                >
                  {loading ? 'Criando...' : 'Criar Banca'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </AnimatedModal>
  );
};

export default CriarBancaModal;